import AccommodationIndividualRoomTile from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTile'
import AccommodationIndividualRoomTileHeader from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileHeader'
import AccommodationIndividualRoomTileImage from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileImage'
import AccommodationIndividualRoomTileDetails from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTileDetails'
import AccommodationIndividualRoomTilePricing from 'components/AccommodationKit/AccommodationMultiRoom/AccommodationIndividualRoomTilePricing'
import Sheet from 'components/utils/Sheet'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useBedbankPackageViewsForIndividualRoom } from 'hooks/useBedbankPackageViews'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import GeoContext from 'contexts/geoContext'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import AccommodationOfferInclusionsExpandable from 'components/OfferPage/Accommodation/AccommodationOfferInclusionsExpandable'
import BedbankMultiIndividualOptionTilePricing from './BedbankMultiIndividualOptionTilePricing'
import TextButton from 'LuxKit/Button/TextButton'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import Caption from 'LuxKit/Typography/Caption'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { rem } from 'polished'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import BedbankIndividualRoomPackageSelectionModal from '../Modals/BedbankIndividualRoomPackageSelectionModal'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import BedbankPromotionDetails from '../BedbankPromotionDetails'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import Clickable from 'components/Common/Clickable'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  packageNumber?: number
  room: OfferPageRoom
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer
  checkInDate: string | undefined
  checkOutDate: string | undefined
  flightOrigin: App.Airport | undefined
  flightsEnabled: boolean
  isFlightsSelected: boolean
  isLoading?: boolean
}

function BedbankMultiIndividualRoomRoom({
  room,
  rooms,
  offer,
  packageNumber,
  checkInDate,
  checkOutDate,
  flightOrigin,
  flightsEnabled,
  isFlightsSelected,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const [packageViews, fetchingRates] = useBedbankPackageViewsForIndividualRoom(offer, room)

  const [packageView, rate] = useMemo(() => {
    const view = packageViews.find(v => v.package.roomId === room.roomId)
    const rate = view?.effectiveRates.find(r => r.id === room.rateId)
    return [view, rate]
  }, [room.rateId, room.roomId, packageViews])

  const totalFlightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const roomPax = sumUpOccupancies([room])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined
  const hasFlights = !!flightPrice

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected
  const validCheckinCheckout = !!(checkInDate && checkOutDate)

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const showModal = useContext(ModalContext)

  const offerPageDispatch = useOfferPageDispatch()

  const isDesktopLayout = useScreenSizeUp('tablet')

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch])

  const showRoomDetails = useCallback(() => {
    if (packageView) {
      showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={packageView.package} />)
    }
  }, [packageView, showModal])

  const onClickChangeRoom = useCallback(() => {
    showModal(<BedbankIndividualRoomPackageSelectionModal roomId={room.id} />)
  }, [showModal, room.id])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!rate) { return [] }
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: rate.totals.total,
        memberPrice: rate.totals.memberTotal,
        propertyFees: rate.totals.propertyFees ?? 0,
        propertyId: offer.property.id,
        offerId: offer.id,
        propertyCountryCode: offer.property.address.countryCode,
        currentRegionCode,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
      }),
    ]
  }, [canShowFlightBundlePrice, currentRegionCode, flightPrice, hideHotelAndFlightsPriceBlock, offer, rate])

  const availablePackages = useMemo(() => packageViews.filter(packageView => packageView.show && packageView.available), [packageViews])

  const onClickLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // After the rates are fetched, there's a small delay before the package autoselection runs (in an effect).
  // This causes the tile to briefly show a "No room available" message. Adding a new "room not selected yet" state
  // would be a pain, so let's just bridge the time gap between rates loading and rooms being selected by extending
  // the loading state by 200 milliseconds.
  const showAsFetchingWhilePackageMissing = useDelayedTogglingBoolean(fetchingRates, { offDelay: 200 }) && !packageView

  if (!packageView) {
    if (showAsFetchingWhilePackageMissing) {
      return <Sheet padding="20 0" background="neutral-eight">
        <LoadingIndicator inline />
      </Sheet>
    } else if (packageViews.every(view => !allRoomsOccupantsFitRoomCapacity(room, view.package.capacities.combinations, offer))) {
      // No availability due to occupants exceeding capacity for all room types
      const noRoomHeading = intl.formatMessage({
        id: 'DNyErDFAncOy7I3Z_qyf',
        defaultMessage: 'No room available',
        description: 'Heading when no room is available due to guest capacity',
      })
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading={noRoomHeading} packageNumber={packageNumber} offer={offer} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            <FormattedMessage
              id="bwCYhUlGQ0W36a-ITcZN"
              defaultMessage="No room available, please <link>revise number of guests</link>"
              description="Warning when no room is available due to guest count, with link to revise guests"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    } else {
      // No availability due to dates
      const noRoomHeadingDates = intl.formatMessage({
        id: 'z0fViwO596vQfHWXe6K4',
        defaultMessage: 'No room available',
        description: 'Heading when no room is available due to dates on the offer detail page',
      })
      return <AccommodationIndividualRoomTile>
        <AccommodationIndividualRoomTileHeader room={room} heading={noRoomHeadingDates} packageNumber={packageNumber} offer={offer} />
        <AccommodationIndividualRoomTileDetails>
          <MessageBanner
            kind="warning"
            density="compact"
          >
            <FormattedMessage
              id="ogx5sHI4ip0IwOW81W3E"
              defaultMessage="No room available, please <datesLink>try different dates</datesLink> or <guestsLink>revise number of guests</guestsLink>"
              description="Warning when no room is available, with links to change dates or revise guest count"
              values={{
                datesLink: (chunks) => <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>{chunks}</TextLink>,
                guestsLink: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>
        </AccommodationIndividualRoomTileDetails>
      </AccommodationIndividualRoomTile>
    }
  }

  const { promotions } = packageView
  const promotion = promotions[rate?.promotionId ?? '']

  const bookingsCount = promotions.length ? packageView.package.stats.booked : undefined
  const canSelectRoom = packageView.available && packageView.capacityValid && packageView.show
  const isOnlyOption = availablePackages.length === 1 && availablePackages[0].rates.length === 1

  const logInToViewPricesLabel = intl.formatMessage({
    id: 'dStdY6MBGWREHZ8eJUQj',
    defaultMessage: 'Log in to view prices',
    description: 'Button text prompting login to see room prices on bedbank multi individual room tile',
  })
  const changeRoomLabel = intl.formatMessage({
    id: '8cEGPZ4fNjtsBKUPX3da',
    defaultMessage: 'Change room',
    description: 'Button text to change selected room on bedbank multi individual room tile',
  })
  const noAdditionalOptionsWalledLabel = intl.formatMessage({
    id: 'dm0Zm6MImKgmOVxTOVeD',
    defaultMessage: 'No additional options',
    description: 'Label when only one room option is available on bedbank multi-room walled content',
  })
  const roomOptionsWalledLabel = intl.formatMessage(
    {
      id: 'lKMZp4Hu_iVZsXJ0liFE',
      defaultMessage: '{count, plural, one {# room option} other {# room options}} available',
      description: 'Count of available room options on bedbank multi-room walled content',
    },
    { count: availablePackages.length },
  )
  const noAdditionalOptionsLabel = intl.formatMessage({
    id: 'ekhSTLtBwLTzprYUFG1X',
    defaultMessage: 'No additional options',
    description: 'Label when only one room option is available on bedbank multi-room tile',
  })
  const roomOptionsLabel = intl.formatMessage(
    {
      id: 'RjYZUTmV8byc5kx1Gy_-',
      defaultMessage: '{count, plural, one {# room option} other {# room options}} available',
      description: 'Count of available room options on bedbank multi-room tile',
    },
    { count: availablePackages.length },
  )

  return <AccommodationIndividualRoomTile roomPackage={packageView.package}>
    <AccommodationIndividualRoomTileHeader
      room={room}
      heading={packageView.package.name ?? promotion?.name}
      packageNumber={packageNumber}
      offer={offer}
      onTitleClick={showRoomDetails}
    />
    <AccommodationIndividualRoomTileImage roomPackage={packageView.package} offer={offer} />
    <AccommodationIndividualRoomTileDetails>
      <LabelGroup>
        {!!bookingsCount && <BookingsCountLabel bookings={bookingsCount} threshold="room" />}
      </LabelGroup>
      <div>
        <Clickable onClick={showRoomDetails}>
          <AccommodationRoomDetailsList roomPackage={packageView.package} />
        </Clickable>
      </div>
      <div>
        {promotion && <BedbankPromotionDetails
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          promotion={promotion}
          rate={rate}
        />}
        {!promotion && rate && <BedbankCancellationPolicy
          rate={rate}
          showNonCancellation
        />}
      </div>
      {rate && <LoyaltyPointsWrapper>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="exact"
        />
      </LoyaltyPointsWrapper>}
      {rate && <AccommodationOfferInclusionsExpandable
        content={promotion?.rateInclusionsLong}
        inclusions={promotion?.inclusions ?? rate.inclusions}
        maxHeight={80}
        collapsedHeight={70}
        additionalContent={<LoyaltyInclusions offer={offer} variant="offer-page" />}
      />}
      {!rate && <LoyaltyInclusions offer={offer} variant="offer-page" />}
    </AccommodationIndividualRoomTileDetails>
    <WalledContent
      enforced={offer.walledGarden}
      hidePricing={offer.hidePricing}
      wall={<AccommodationIndividualRoomTilePricing desktopVerticalAlign="start">
        <TextButton
          kind="primary"
          size="medium"
          onClick={onClickLogin}
          fit="full-width"
        >
          {logInToViewPricesLabel}
        </TextButton>
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="dark" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={isOnlyOption}>{changeRoomLabel}</TextButton>
          <Caption variant="medium" colour="neutral-three">
            {isOnlyOption ? noAdditionalOptionsWalledLabel : roomOptionsWalledLabel}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>}
    >
      <AccommodationIndividualRoomTilePricing>
        {!fetchingRates && validCheckinCheckout && !canSelectRoom && <>
          {!packageView.capacityValid && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading={intl.formatMessage({
              id: 'QiH7fGiMn7e0EvMvTE8a',
              defaultMessage: 'This room cannot accommodate your number of guests',
              description: 'Heading for message banner when room capacity is exceeded',
            })}
          >
            <FormattedMessage
              id="24d5LxOxd2-_yCEiIrMF"
              defaultMessage="Choose another room or <link>add more rooms</link>"
              description="Suggestion to choose another room or add more rooms when capacity is exceeded"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
          {(!packageView.available || !packageView.show) && packageView.capacityValid && <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading={intl.formatMessage({
              id: '-UHWtU07oDbtN21RYH2s',
              defaultMessage: 'This room is sold out on your dates or max number of guests exceeded',
              description: 'Heading for message banner when room is sold out or max guests exceeded',
            })}
          >
            <FormattedMessage
              id="JJoG1Er7O-Bb4lzZ5sQX"
              defaultMessage="Choose another room or <link>try different dates</link>"
              description="Suggestion to choose another room or try different dates when room is sold out"
              values={{
                link: (chunks) => <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
        </>}
        {fetchingRates && <LoadingIndicator inline />}
        {rate && <BedbankMultiIndividualOptionTilePricing
          promotion={promotion}
          rate={rate}
          bundleRate={rate}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={0}
          showPoints={isDesktopLayout}
          discountPosition="valueRow"
        />}
        <Group direction="vertical" gap={8} verticalAlign="end" horizontalAlign="center">
          <TextButton fit="full-width" variant="dark" kind="secondary" size="medium" startIcon={<LineEditAltIcon />} onClick={onClickChangeRoom} disabled={isOnlyOption}>{changeRoomLabel}</TextButton>
          <Caption variant="medium" colour="neutral-three">
            {isOnlyOption ? noAdditionalOptionsLabel : roomOptionsLabel}
          </Caption>
        </Group>
      </AccommodationIndividualRoomTilePricing>
    </WalledContent>
  </AccommodationIndividualRoomTile>
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flightOrigin'
  | 'flightsEnabled'
  | 'isFlightsSelected'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.bedbankOffer!,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  flightOrigin: state.airport,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
}), BedbankMultiIndividualRoomRoom)
